import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/config';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

interface SaveResult {
  success: boolean;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// PartnerForm
// ---------------------------------------------------------------------------

interface PartnerData {
  name?: string;
  primaryUserName?: string;
  email?: string;
  status?: string;
  membershipLevel?: string;
}

interface PartnerFormProps {
  partner?: PartnerData | null;
  onSave: (data: PartnerFormState) => Promise<SaveResult | null>;
  onCancel: () => void;
}

interface PartnerFormState {
  name: string;
  primaryUserName: string;
  email: string;
  status: string;
  membershipLevel: string;
}

export const PartnerForm: React.FC<PartnerFormProps> = ({ partner, onSave, onCancel }) => {
  const [formData, setFormData] = useState<PartnerFormState>({
    name: partner?.name ?? '',
    primaryUserName: partner?.primaryUserName ?? '',
    email: partner?.email ?? '',
    status: partner?.status ?? 'ACTIVE',
    membershipLevel: partner?.membershipLevel ?? 'Standard',
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const result = await onSave(formData);
    if (result?.success) {
      onCancel();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="partner-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Partner Name</label>
        <input
          id="partner-name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          placeholder="e.g., TechCorp Solutions"
          required
        />
      </div>
      <div>
        <label htmlFor="contact-person-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contact Person Name</label>
        <input
          id="contact-person-name"
          type="text"
          value={formData.primaryUserName}
          onChange={(e) => setFormData({ ...formData, primaryUserName: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          placeholder="e.g., John Smith"
          required
        />
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Full name of the person who will receive the account setup email
        </p>
      </div>
      <div>
        <label htmlFor="contact-person-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contact Person Email</label>
        <input
          id="contact-person-email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>
      <div>
        <label htmlFor="partner-status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
        <select
          id="partner-status"
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="SUSPENDED">Suspended</option>
        </select>
      </div>
      <div>
        <label htmlFor="membership-level" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Membership Level</label>
        <select
          id="membership-level"
          value={formData.membershipLevel}
          onChange={(e) => setFormData({ ...formData, membershipLevel: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="Standard">Standard</option>
          <option value="Premium">Premium</option>
          <option value="Enterprise">Enterprise</option>
        </select>
      </div>
      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
        >
          Cancel
        </button>
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
          Save
        </button>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// ClientForm
// ---------------------------------------------------------------------------

interface ClientData {
  name?: string;
  email?: string;
  partner?: string;
  contactPerson?: string;
  subscription_plan?: string;
  subscription_status?: string;
  trial_ends_at?: string;
  subscription_started_at?: string;
}

interface ClientFormState {
  name: string;
  email: string;
  partner: string;
  contactPerson: string;
  subscription_plan: string;
  subscription_status: string;
  trial_ends_at: string;
  subscription_started_at: string;
}

interface PartnerListItem {
  id: string;
  name: string;
}

interface ClientFormProps {
  client?: ClientData | null;
  onSave: (data: ClientFormState) => Promise<SaveResult | null>;
  onCancel: () => void;
}

export const ClientForm: React.FC<ClientFormProps> = ({ client, onSave, onCancel }) => {
  const [formData, setFormData] = useState<ClientFormState>({
    name: client?.name ?? '',
    email: client?.email ?? '',
    partner: client?.partner ?? '',
    contactPerson: client?.contactPerson ?? '',
    subscription_plan: client?.subscription_plan ?? 'monthly',
    subscription_status: client?.subscription_status ?? 'active',
    trial_ends_at: client?.trial_ends_at ? client.trial_ends_at.split('T')[0] : '',
    subscription_started_at: client?.subscription_started_at
      ? client.subscription_started_at.split('T')[0]
      : '',
  });
  const [partners, setPartners] = useState<PartnerListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/partners`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const partnersData = (await response.json()) as PartnerListItem[];
          setPartners(partnersData);
        }
      } catch (err: unknown) {
        console.error('Error loading partners:', err);
      }
      setLoading(false);
    };

    loadPartners();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const result = await onSave(formData);
    if (result?.success) {
      onCancel();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="company-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company Name</label>
        <input
          id="company-name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>
      <div>
        <label htmlFor="client-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
        <input
          id="client-email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>
      <div>
        <label htmlFor="client-partner" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Partner</label>
        <select
          id="client-partner"
          value={formData.partner}
          onChange={(e) => setFormData({ ...formData, partner: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
          disabled={loading}
        >
          <option value="">Select Partner</option>
          {partners.map((partner) => (
            <option key={partner.id} value={partner.id}>
              {partner.name}
            </option>
          ))}
        </select>
        {loading && <p className="text-sm text-gray-500 mt-1">Loading partners...</p>}
      </div>
      <div>
        <label htmlFor="contact-person" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Contact Person</label>
        <input
          id="contact-person"
          type="text"
          value={formData.contactPerson}
          onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>
      <div>
        <label htmlFor="subscription-plan" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Subscription Plan</label>
        <select
          id="subscription-plan"
          value={formData.subscription_plan}
          onChange={(e) => setFormData({ ...formData, subscription_plan: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="assessment_only">Assessment Only</option>
          <option value="trial">Trial</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
          <option value="professional_services">Professional Services</option>
          <option value="managed_services">Managed Services</option>
        </select>
      </div>
      <div>
        <label htmlFor="subscription-status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Subscription Status</label>
        <select
          id="subscription-status"
          value={formData.subscription_status}
          onChange={(e) => setFormData({ ...formData, subscription_status: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      {formData.subscription_plan === 'trial' && (
        <div>
          <label htmlFor="trial-end-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Trial End Date</label>
          <input
          id="trial-end-date"
            type="date"
            value={formData.trial_ends_at}
            onChange={(e) => setFormData({ ...formData, trial_ends_at: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Set when the trial period should end</p>
        </div>
      )}
      {(['monthly', 'yearly'].includes(formData.subscription_plan)) && (
        <div>
          <label htmlFor="subscription-start-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Subscription Start Date
          </label>
          <input
          id="subscription-start-date"
            type="date"
            value={formData.subscription_started_at}
            onChange={(e) => setFormData({ ...formData, subscription_started_at: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Start date for calculating renewal (monthly: +1 month, yearly: +1 year)
          </p>
        </div>
      )}
      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          disabled={loading}
        >
          Save
        </button>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// ProjectForm
// ---------------------------------------------------------------------------

interface ProjectData {
  name?: string;
  clientId?: string;
  client_id?: string;
  status?: string;
  infrastructureBudget?: string | number;
  deliveryBudget?: string | number;
  description?: string;
}

interface ProjectFormState {
  name: string;
  clientId: string;
  status: string;
  infrastructureBudget: string | number;
  deliveryBudget: string | number;
  description: string;
}

interface ClientListItem {
  id: string;
  name: string;
}

interface ProjectFormProps {
  project?: ProjectData | null;
  onSave: (data: ProjectFormState) => Promise<SaveResult | null>;
  onCancel: () => void;
}

export const ProjectForm: React.FC<ProjectFormProps> = ({ project, onSave, onCancel }) => {
  const [formData, setFormData] = useState<ProjectFormState>({
    name: project?.name ?? '',
    clientId: project?.clientId ?? project?.client_id ?? '',
    status: project?.status ?? 'PLANNING',
    infrastructureBudget: project?.infrastructureBudget ?? '',
    deliveryBudget: project?.deliveryBudget ?? '',
    description: project?.description ?? '',
  });
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadClients = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/clients`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const clientsData = (await response.json()) as ClientListItem[];
          setClients(clientsData);
        }
      } catch (err: unknown) {
        console.error('Error loading clients:', err);
      }
      setLoading(false);
    };

    loadClients();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const result = await onSave(formData);
    if (result?.success) {
      onCancel();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="project-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Project Name</label>
        <input
          id="project-name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>
      <div>
        <label htmlFor="project-client" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client</label>
        <select
          id="project-client"
          value={formData.clientId}
          onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
          disabled={loading}
        >
          <option value="">Select Client</option>
          {clients.map((client) => (
            <option key={client.id} value={client.id}>
              {client.name}
            </option>
          ))}
        </select>
        {loading && <p className="text-sm text-gray-500 mt-1">Loading clients...</p>}
      </div>
      <div>
        <label htmlFor="project-status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
        <select
          id="project-status"
          value={formData.status}
          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="PLANNING">Planning</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="COMPLETED">Completed</option>
          <option value="ON_HOLD">On Hold</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
      </div>
      <div>
        <label htmlFor="infrastructure-budget" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Infrastructure Budget
        </label>
        <input
          id="infrastructure-budget"
          type="number"
          value={formData.infrastructureBudget}
          onChange={(e) => setFormData({ ...formData, infrastructureBudget: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          placeholder="Cloud and AI services budget"
        />
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Budget for cloud infrastructure and AI services
        </p>
      </div>
      <div>
        <label htmlFor="delivery-budget" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Delivery Budget</label>
        <input
          id="delivery-budget"
          type="number"
          value={formData.deliveryBudget}
          onChange={(e) => setFormData({ ...formData, deliveryBudget: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          placeholder="Human hours and delivery budget"
        />
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Budget for human hours and delivery services
        </p>
      </div>
      <div>
        <label htmlFor="project-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
        <textarea
          id="project-description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>
      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          disabled={loading}
        >
          Save
        </button>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// AwsUserForm
// ---------------------------------------------------------------------------

interface AccessibleClient {
  name: string;
  access_source?: string;
  access_type: string;
  modules?: string[];
}

interface AwsUserData {
  name?: string;
  email?: string;
  job_title?: string;
  partners?: string[];
  accessible_clients?: AccessibleClient[];
  aws_accessible_modules?: string[];
}

interface AwsUserFormState {
  name: string;
  email: string;
  job_title: string;
  partners: string[];
  clients: { name: string; access_type: string; modules?: string[] }[];
  modules: string[];
}

interface AwsUserFormProps {
  awsUser?: AwsUserData | null;
  onSave: (data: AwsUserFormState) => Promise<SaveResult | null>;
  onCancel: () => void;
}

const MODULE_LABELS: Record<string, string> = {
  genaiops: 'GenAIOps',
  agentops: 'AgentOps',
};

const getModuleLabel = (module: string): string =>
  MODULE_LABELS[module] ?? (module.charAt(0).toUpperCase() + module.slice(1));

export const AwsUserForm: React.FC<AwsUserFormProps> = ({ awsUser, onSave, onCancel }) => {
  const [formData, setFormData] = useState<AwsUserFormState>({
    name: awsUser?.name ?? '',
    email: awsUser?.email ?? '',
    job_title: awsUser?.job_title ?? '',
    partners: awsUser?.partners ?? [],
    clients:
      awsUser?.accessible_clients
        ?.filter((c) => c.access_source === 'direct')
        ?.map((c) => ({
          name: c.name,
          access_type: c.access_type,
          modules: c.modules ?? [],
        })) ?? [],
    modules: awsUser?.aws_accessible_modules ?? [],
  });
  const [partners, setPartners] = useState<PartnerListItem[]>([]);
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        };

        const partnersResponse = await fetch(`${API_BASE_URL}/partners`, { headers });
        if (partnersResponse.ok) {
          const partnersData = (await partnersResponse.json()) as PartnerListItem[];
          setPartners(partnersData);
        }

        const clientsResponse = await fetch(`${API_BASE_URL}/clients`, { headers });
        if (clientsResponse.ok) {
          const clientsData = (await clientsResponse.json()) as (ClientListItem & { partner?: string })[];
          setClients(clientsData);
        }
      } catch (err: unknown) {
        console.error('Error loading data:', err);
      }
      setLoading(false);
    };

    loadData();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const result = await onSave(formData);
    if (result?.success) {
      onCancel();
    }
  };

  const handlePartnerToggle = (partnerName: string) => {
    const newPartners = formData.partners.includes(partnerName)
      ? formData.partners.filter((p) => p !== partnerName)
      : [...formData.partners, partnerName];
    setFormData({ ...formData, partners: newPartners });
  };

  const handleClientToggle = (clientName: string) => {
    const existingClient = formData.clients.find((c) => c.name === clientName);
    if (existingClient) {
      setFormData({ ...formData, clients: formData.clients.filter((c) => c.name !== clientName) });
    } else {
      setFormData({
        ...formData,
        clients: [...formData.clients, { name: clientName, access_type: 'read_write' }],
      });
    }
  };

  const handleClientAccessTypeChange = (
    clientName: string,
    accessType: string,
    modules: string[] | null = null
  ) => {
    setFormData({
      ...formData,
      clients: formData.clients.map((c) => {
        if (c.name === clientName) {
          const updated = { ...c, access_type: accessType };
          if (modules !== null) {
            updated.modules = modules;
          }
          return updated;
        }
        return c;
      }),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="aws-user-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
        <input
          id="aws-user-name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>
      <div>
        <label htmlFor="aws-user-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
        <input
          id="aws-user-email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>
      <div>
        <label htmlFor="aws-user-job-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Job Title / Role</label>
        <select
          id="aws-user-job-title"
          value={formData.job_title}
          onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        >
          <option value="">Select Job Title</option>
          <optgroup label="1st Level">
            <option value="account_manager">Account Manager</option>
            <option value="aws_technical_sa">AWS Technical (SA)</option>
            <option value="aws_technical_tam">AWS Technical (TAM)</option>
          </optgroup>
          <optgroup label="2nd Level">
            <option value="district_manager">District Manager</option>
          </optgroup>
          <optgroup label="3rd Level">
            <option value="partner_sales_manager">Partner Sales Manager</option>
          </optgroup>
          <optgroup label="4th Level">
            <option value="area_sales_manager">Area Sales Manager</option>
            <option value="sales_leader">Sales Leader</option>
          </optgroup>
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Select the AWS user&apos;s job title/role in the organization hierarchy
        </p>
      </div>
      <div>
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Default Module Access</span>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">
          Hub is always accessible. Select additional modules this AWS user can access by default.
        </p>
        <div className="mt-2 space-y-2">
          {['platformops', 'dataops', 'genaiops', 'agentops'].map((module) => (
            <label key={module} className="flex items-center">
              <input
                type="checkbox"
                checked={formData.modules.includes(module)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({ ...formData, modules: [...formData.modules, module] });
                  } else {
                    setFormData({ ...formData, modules: formData.modules.filter((m) => m !== module) });
                  }
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                {getModuleLabel(module)}
              </span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Partner Access</span>
        {loading ? (
          <p className="text-sm text-gray-500 mt-2">Loading partners...</p>
        ) : (
          <div className="mt-2 space-y-2">
            {partners.map((partner) => (
              <label key={partner.id} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.partners.includes(partner.name)}
                  onChange={() => handlePartnerToggle(partner.name)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{partner.name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div>
        <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">Direct Client Access</span>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Grant access to specific clients. You can also grant module access per client (clients can override default
          module access).
        </p>
        {loading ? (
          <p className="text-sm text-gray-500 mt-2">Loading clients...</p>
        ) : (
          <div className="mt-2 space-y-3 max-h-48 overflow-y-auto">
            {(clients as (ClientListItem & { partner?: string })[]).map((client) => {
              const isAssigned = formData.clients.find((c) => c.name === client.name);
              return (
                <div key={client.id} className="flex items-center justify-between p-2 border rounded-md">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={!!isAssigned}
                      onChange={() => handleClientToggle(client.name)}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <div className="ml-2">
                      <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{client.name}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 block">
                        Partner: {client.partner ?? 'Unknown'}
                      </span>
                    </div>
                  </div>
                  {isAssigned && (
                    <div className="flex flex-col gap-2">
                      <select
                        value={isAssigned.access_type}
                        onChange={(e) => handleClientAccessTypeChange(client.name, e.target.value)}
                        className="text-xs border-gray-300 rounded focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="read_only">Read Only</option>
                        <option value="read_write">Read/Write</option>
                        <option value="admin">Admin</option>
                      </select>
                      <div className="text-xs">
                        <span className="block text-gray-600 dark:text-gray-400 mb-1">Module Access:</span>
                        <div className="space-y-1">
                          {['platformops', 'dataops', 'genaiops', 'agentops'].map((module) => (
                            <label key={module} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={isAssigned.modules?.includes(module) ?? false}
                                onChange={(e) => {
                                  const clientModules = isAssigned.modules ?? [];
                                  const updatedModules = e.target.checked
                                    ? [...clientModules, module]
                                    : clientModules.filter((m) => m !== module);
                                  handleClientAccessTypeChange(
                                    client.name,
                                    isAssigned.access_type,
                                    updatedModules
                                  );
                                }}
                                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                              />
                              <span className="ml-1 text-gray-700 dark:text-gray-300 capitalize">
                                {(MODULE_LABELS[module] ?? module.charAt(0).toUpperCase() + module.slice(1)).replace('GenAIOps', 'GenAI').replace('AgentOps', 'Agent')}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          disabled={loading}
        >
          Save
        </button>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// UserForm
// ---------------------------------------------------------------------------

interface UserData {
  name?: string;
  email?: string;
  role?: string;
  organization?: string;
  partnerId?: string | null;
  clientId?: string | null;
}

interface UserFormState {
  name: string;
  email: string;
  role: string;
  organization: string;
  partner_id: string | null;
  client_id: string | null;
}

interface OrgOption {
  value: string;
  label: string;
}

interface UserFormProps {
  user?: UserData | null;
  onSave: (data: Omit<UserFormState, 'organization'>) => Promise<SaveResult | null>;
  onCancel: () => void;
}

export const UserForm: React.FC<UserFormProps> = ({ user, onSave, onCancel }) => {
  const [formData, setFormData] = useState<UserFormState>({
    name: user?.name ?? '',
    email: user?.email ?? '',
    role: user?.role ?? 'client',
    organization: user?.organization ?? '',
    partner_id: user?.partnerId ?? null,
    client_id: user?.clientId ?? null,
  });
  const [partners, setPartners] = useState<PartnerListItem[]>([]);
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        };

        const partnersRes = await fetch(`${API_BASE_URL}/partners`, { headers });
        if (partnersRes.ok) {
          const partnersData = (await partnersRes.json()) as PartnerListItem[];
          setPartners(partnersData);
        }

        const clientsRes = await fetch(`${API_BASE_URL}/clients`, { headers });
        if (clientsRes.ok) {
          const clientsData = (await clientsRes.json()) as ClientListItem[];
          setClients(clientsData);
        }
      } catch (err: unknown) {
        console.error('Error loading organizations:', err);
      }
      setLoading(false);
    };

    loadOrganizations();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Exclude organization (display name) from API data
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { organization, ...apiData } = formData;
    const result = await onSave(apiData);
    if (result?.success) {
      onCancel();
    }
  };

  const getOrganizationOptions = (): OrgOption[] => {
    if (formData.role === 'partner') {
      return partners.map((p) => ({ value: p.id, label: p.name }));
    }
    if (formData.role === 'client') {
      return clients.map((c) => ({ value: c.id, label: c.name }));
    }
    return [];
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="user-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
        <input
          id="user-name"
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>
      <div>
        <label htmlFor="user-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
        <input
          id="user-email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>
      <div>
        <label htmlFor="user-role" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
        <select
          id="user-role"
          value={formData.role}
          onChange={(e) =>
            setFormData({ ...formData, role: e.target.value, organization: '', partner_id: null, client_id: null })
          }
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="partner">Partner</option>
          <option value="client">Client</option>
          <option value="aws">AWS</option>
        </select>
      </div>
      {(['partner', 'client'].includes(formData.role)) && (
        <div>
          <label htmlFor="user-organization" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {formData.role === 'partner' ? 'Partner' : 'Client'}
          </label>
          <select
            id="user-organization"
            value={formData.role === 'partner' ? (formData.partner_id ?? '') : (formData.client_id ?? '')}
            onChange={(e) => {
              const selectedId = e.target.value || null;
              const selectedOrg = getOrganizationOptions().find((o) => o.value === selectedId);
              setFormData({
                ...formData,
                organization: selectedOrg?.label ?? '',
                partner_id: formData.role === 'partner' ? selectedId : null,
                client_id: formData.role === 'client' ? selectedId : null,
              });
            }}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            required
            disabled={loading}
          >
            <option value="">Select {formData.role === 'partner' ? 'Partner' : 'Client'}</option>
            {getOrganizationOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {loading && <p className="text-sm text-gray-500 mt-1">Loading...</p>}
        </div>
      )}
      {!user && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          The user will receive an email to set their password.
        </p>
      )}
      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          disabled={loading}
        >
          Save
        </button>
      </div>
    </form>
  );
};
